import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { CATEGORIES } from "@/lib/categories";
import { useTopicSearch } from "@/hooks/useArticles";
import {
  useLocalDraft,
  type TokenSymbol,
  type TokenPrice,
} from "@/hooks/useLocalDraft";
import { TiptapEditor } from "@/components/editor/TiptapEditor";
import type { Article } from "@/lib/api";

const TOKEN_LABELS: { [K in TokenSymbol]: string } = {
  USDC: "USDC (Arbitrum)",
  EURC: "EURC (Base)",
};

function initTokens(initial?: Article): TokenPrice[] {
  const tokens: TokenPrice[] = [];
  if (initial?.priceUsdc) tokens.push({ symbol: "USDC", amount: initial.priceUsdc });
  if (initial?.priceEurc) tokens.push({ symbol: "EURC", amount: initial.priceEurc });
  if (tokens.length === 0) tokens.push({ symbol: "USDC", amount: "0.50" });
  return tokens;
}

interface ArticleEditorProps {
  initial?: Article;
  initialContent?: string;
  onSubmit: (data: {
    title: string;
    subtitle: string;
    coverImageUrl: string;
    contentMarkdown: string;
    previewMarkdown: string;
    priceUsdc: string;
    priceEurc: string;
    category: string;
    topicNames: string[];
  }) => void;
  loading?: boolean;
  draftKey?: string;
  onDraftCleared?: () => void;
}

function TopicInput({
  topics,
  onChange,
}: {
  topics: string[];
  onChange: (topics: string[]) => void;
}) {
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { data: suggestions } = useTopicSearch(input);

  const addTopic = (name: string) => {
    const normalized = name.toLowerCase().trim();
    if (normalized && !topics.includes(normalized) && topics.length < 5) {
      onChange([...topics, normalized]);
    }
    setInput("");
    setShowSuggestions(false);
  };

  const removeTopic = (topic: string) => {
    onChange(topics.filter((t) => t !== topic));
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-body font-bold text-grey">
        Topics (max 5)
      </label>
      {topics.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {topics.map((topic) => (
            <span
              key={topic}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-beige/10 border border-beige/30 rounded-full text-xs font-body"
            >
              {topic}
              <button
                type="button"
                onClick={() => removeTopic(topic)}
                className="text-grey hover:text-red-500"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
      {topics.length < 5 && (
        <div className="relative">
          <Input
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && input.trim()) {
                e.preventDefault();
                addTopic(input);
              }
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            onFocus={() => input && setShowSuggestions(true)}
            placeholder="Type a topic and press Enter..."
          />
          {showSuggestions && suggestions && suggestions.length > 0 && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-beige/40 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {suggestions
                .filter((s) => !topics.includes(s.name))
                .map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addTopic(s.name)}
                    className="w-full text-left px-3 py-2 text-sm font-body hover:bg-beige/10 flex items-center justify-between"
                  >
                    <span>{s.name}</span>
                    <span className="text-xs text-beige">{s.count} articles</span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ArticleEditor({
  initial,
  initialContent,
  onSubmit,
  loading,
  draftKey,
  onDraftCleared,
}: ArticleEditorProps) {
  const draft = useLocalDraft(draftKey);
  // Load draft on first render (before state is used)
  const [restoredDraft] = useState(() => (draftKey ? draft.load() : null));
  // The "Draft restored" banner shows from mount if a draft was loaded
  const [draftRestored, setDraftRestored] = useState(restoredDraft !== null);

  const [title, setTitle] = useState(
    restoredDraft?.title ?? initial?.title ?? "",
  );
  const [subtitle, setSubtitle] = useState(
    restoredDraft?.subtitle ?? initial?.subtitle ?? "",
  );
  const [coverImageUrl, setCoverImageUrl] = useState(
    restoredDraft?.coverImageUrl ?? initial?.coverImageUrl ?? "",
  );
  const [contentMarkdown, setContentMarkdown] = useState(
    restoredDraft?.contentMarkdown ?? initialContent ?? "",
  );
  const [previewMarkdown, setPreviewMarkdown] = useState(
    restoredDraft?.previewMarkdown ?? initial?.previewMarkdown ?? "",
  );
  const [tokens, setTokens] = useState<TokenPrice[]>(
    restoredDraft?.tokens ?? (() => initTokens(initial)),
  );
  const [category, setCategory] = useState(
    restoredDraft?.category ?? initial?.category ?? "",
  );
  const [topics, setTopics] = useState<string[]>(
    restoredDraft?.topics ?? initial?.topics ?? [],
  );

  // Autosave on every field change (debounced inside the hook)
  useEffect(() => {
    draft.save({
      title,
      subtitle,
      coverImageUrl,
      contentMarkdown,
      previewMarkdown,
      tokens,
      category,
      topics,
    });
  }, [
    title, subtitle, coverImageUrl, contentMarkdown,
    previewMarkdown, tokens, category, topics, draft,
  ]);

  const presentSymbols = new Set(tokens.map((t) => t.symbol));
  const missingSymbol: TokenSymbol | null =
    !presentSymbols.has("USDC") ? "USDC" :
    !presentSymbols.has("EURC") ? "EURC" :
    null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const priceUsdc = tokens.find((t) => t.symbol === "USDC")?.amount || "";
    const priceEurc = tokens.find((t) => t.symbol === "EURC")?.amount || "";
    onSubmit({
      title,
      subtitle,
      coverImageUrl,
      contentMarkdown,
      previewMarkdown,
      priceUsdc,
      priceEurc,
      category,
      topicNames: topics,
    });
  };

  const discardDraft = () => {
    draft.clear();
    setTitle(initial?.title || "");
    setSubtitle(initial?.subtitle || "");
    setCoverImageUrl(initial?.coverImageUrl || "");
    setContentMarkdown(initialContent || "");
    setPreviewMarkdown(initial?.previewMarkdown || "");
    setTokens(initTokens(initial));
    setCategory(initial?.category || "");
    setTopics(initial?.topics || []);
    setDraftRestored(false);
    onDraftCleared?.();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card className="p-6 space-y-4">
        {draftRestored && (
          <div className="flex items-center justify-between px-3 py-2 bg-orange/10 border border-orange/30 rounded-lg text-sm font-body">
            <span>Draft restored from your last session.</span>
            <button
              type="button"
              onClick={discardDraft}
              className="text-orange hover:text-orange/80 underline ml-2"
            >
              Discard
            </button>
          </div>
        )}
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Your article title"
          required
        />
        <Input
          label="Subtitle"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="A brief subtitle (optional)"
        />
        <Input
          label="Cover Image URL"
          value={coverImageUrl}
          onChange={(e) => setCoverImageUrl(e.target.value)}
          placeholder="https://... (optional)"
        />

        <div className="flex flex-col gap-1">
          <label className="text-sm font-body font-bold text-grey">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2 rounded-lg border border-beige/40 bg-white font-body text-sm focus:outline-none focus:ring-2 focus:ring-orange/50"
          >
            <option value="">Uncategorized</option>
            {CATEGORIES.map((cat) => (
              <option key={cat.slug} value={cat.slug}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <TopicInput topics={topics} onChange={setTopics} />

        <div className="flex flex-col gap-1">
          <label className="text-sm font-body font-bold text-grey">
            Preview (shown before paywall)
          </label>
          <TiptapEditor
            content={previewMarkdown}
            onChange={setPreviewMarkdown}
            placeholder="Write a preview that readers see before paying..."
            minHeight="120px"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-body font-bold text-grey">
            Full Content (behind paywall)
          </label>
          <TiptapEditor
            content={contentMarkdown}
            onChange={setContentMarkdown}
            placeholder="Write your full article here..."
            minHeight="400px"
            showWordCount
            showTableOfContents
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-body font-bold text-grey">
            Pricing
          </label>
          {tokens.map((tok, idx) => (
            <div key={tok.symbol} className="flex items-center gap-2">
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={tok.amount}
                onChange={(e) => {
                  const updated = [...tokens];
                  updated[idx] = { ...tok, amount: e.target.value };
                  setTokens(updated);
                }}
                placeholder="0.50"
                required
                className="flex-1"
              />
              <span className="text-sm font-body text-grey whitespace-nowrap w-32">
                {TOKEN_LABELS[tok.symbol]}
              </span>
              {tokens.length > 1 && (
                <button
                  type="button"
                  onClick={() => setTokens(tokens.filter((_, i) => i !== idx))}
                  className="text-grey hover:text-red-500 text-lg px-1"
                  title="Remove token"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
          {missingSymbol && (
            <button
              type="button"
              onClick={() =>
                setTokens([...tokens, { symbol: missingSymbol, amount: "" }])
              }
              className="text-sm text-orange hover:text-orange/80 font-body self-start"
            >
              + Add {TOKEN_LABELS[missingSymbol]} price
            </button>
          )}
        </div>

        <Button type="submit" className="w-full" loading={loading}>
          {initial ? "Update Article" : "Create Article"}
        </Button>
      </Card>
    </form>
  );
}
